import type { Database } from '@/types/database.generated';

type Enums = Database['public']['Enums'];

export type ConsentTypeKey = Enums['consent_type'];
export type ConsentRecordMethod = Enums['consent_record_method'];
export type ConsentRequestStatus = Enums['consent_request_status'];
type ConsentTextVersion = Database['public']['Tables']['consent_text_versions']['Row'];

export const CONSENT_TYPES: readonly ConsentTypeKey[] = [
  'privacy_notice',
  'portal_terms',
  'marketing_email',
  'marketing_sms',
  'photo_video',
  'other_purpose',
  'sl_41_disclosure',
  'sl_43_disclosure',
  'sl_42a_lookup',
  'sl_42d_retrieval',
  'refusal_disclosure',
  'refusal_lookup',
  'journal_transfer',
  'informed_treatment',
];

/** Types a patient can answer in the portal. Mirrors private.consent_type_portal_recordable. */
export const PORTAL_RECORDABLE_CONSENT_TYPES: readonly ConsentTypeKey[] = [
  'privacy_notice',
  'portal_terms',
  'marketing_email',
  'marketing_sms',
  'photo_video',
  'other_purpose',
];

/** Types Clinio ships a default text for (migration 20261106090200). */
export const CLINIO_DEFAULT_TEXT_TYPES: readonly ConsentTypeKey[] = [
  'privacy_notice',
  'portal_terms',
  'marketing_email',
  'marketing_sms',
  'photo_video',
];

/** Methods staff can pick. 'portal' is only ever written by the portal itself. */
export const STAFF_RECORD_METHODS: readonly ConsentRecordMethod[] = ['oral', 'paper_scanned', 'other'];

export type ConsentTypeGroup = 'information' | 'marketing' | 'health_law' | 'refusal' | 'treatment';

export function consentTypeGroup(type: ConsentTypeKey): ConsentTypeGroup {
  switch (type) {
    case 'privacy_notice':
    case 'portal_terms':
      return 'information';
    case 'marketing_email':
    case 'marketing_sms':
    case 'photo_video':
    case 'other_purpose':
      return 'marketing';
    case 'sl_41_disclosure':
    case 'sl_43_disclosure':
    case 'sl_42a_lookup':
    case 'sl_42d_retrieval':
      return 'health_law';
    case 'refusal_disclosure':
    case 'refusal_lookup':
      return 'refusal';
    case 'journal_transfer':
    case 'informed_treatment':
      return 'treatment';
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

export function isPortalRecordableConsentType(type: ConsentTypeKey): boolean {
  return PORTAL_RECORDABLE_CONSENT_TYPES.includes(type);
}

/** sl_43_disclosure and sl_42d_retrieval: a given consent must expire within one year. */
export function consentTypeRequiresExpiry(type: ConsentTypeKey): boolean {
  return type === 'sl_43_disclosure' || type === 'sl_42d_retrieval';
}

/** Health-law consents and refusals are scoped to a recipient (or purpose). */
export function consentTypeUsesRecipient(type: ConsentTypeKey): boolean {
  const group = consentTypeGroup(type);
  return group === 'health_law' || group === 'refusal' || type === 'journal_transfer';
}

export function consentTypeIsRefusal(type: ConsentTypeKey): boolean {
  return consentTypeGroup(type) === 'refusal';
}

/** scope_channel for marketing consents. Mirrors private.consent_default_scope_channel. */
export function consentScopeChannel(type: ConsentTypeKey): string | null {
  if (type === 'marketing_email') return 'email';
  if (type === 'marketing_sms') return 'sms';
  return null;
}

/**
 * Text in force for a clinic: newest clinic version, else newest Clinio-wide version.
 * Mirrors private.consent_effective_text_version.
 */
export function effectiveConsentTextVersion(
  versions: readonly ConsentTextVersion[],
  clinicId: string,
  type: ConsentTypeKey,
  language = 'da',
): ConsentTextVersion | null {
  const newest = (rows: ConsentTextVersion[]) =>
    rows.reduce<ConsentTextVersion | null>((best, row) => {
      if (!best) return row;
      if (row.created_at > best.created_at) return row;
      if (row.created_at === best.created_at && row.id > best.id) return row;
      return best;
    }, null);
  const candidates = versions.filter((v) => v.consent_type === type && v.language === language);
  return (
    newest(candidates.filter((v) => v.clinic_id === clinicId)) ??
    newest(candidates.filter((v) => v.clinic_id === null))
  );
}

/** Versions of one type, newest first, clinic versions before Clinio-wide ones on ties. */
export function sortConsentTextVersions(versions: readonly ConsentTextVersion[]): ConsentTextVersion[] {
  return [...versions].sort((a, b) => {
    if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1;
    if ((a.clinic_id === null) !== (b.clinic_id === null)) return a.clinic_id === null ? 1 : -1;
    return a.id < b.id ? 1 : -1;
  });
}

export function addOneYear(from: Date): Date {
  const next = new Date(from.getTime());
  next.setFullYear(next.getFullYear() + 1);
  return next;
}

/** Date-only (YYYY-MM-DD) one year from `from`, in local time. */
export function oneYearFromDateInput(from: Date): string {
  const d = addOneYear(from);
  d.setDate(d.getDate() - 1);
  return toDateInput(d);
}

export function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** End of the chosen local day as ISO; null for an empty value. */
export function dateInputToExpiryIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 0, 0).toISOString();
}

export type ExpiryValidation = 'ok' | 'required' | 'past' | 'over_one_year';

export function validateConsentExpiry(
  type: ConsentTypeKey,
  decisionIsGiven: boolean,
  expiresAtIso: string | null,
  now: Date = new Date(),
): ExpiryValidation {
  if (!expiresAtIso) {
    return decisionIsGiven && consentTypeRequiresExpiry(type) ? 'required' : 'ok';
  }
  const expires = new Date(expiresAtIso);
  if (expires.getTime() <= now.getTime()) return 'past';
  if (consentTypeRequiresExpiry(type) && expires.getTime() > addOneYear(now).getTime()) {
    return 'over_one_year';
  }
  return 'ok';
}

export function isConsentRequestOpen(
  request: { status: ConsentRequestStatus; expires_at: string },
  now: Date = new Date(),
): boolean {
  return request.status === 'pending' && new Date(request.expires_at).getTime() > now.getTime();
}

/** Readers treat pending + past expires_at as expired even before the expiry job runs. */
export function displayConsentRequestStatus(
  request: { status: ConsentRequestStatus; expires_at: string },
  now: Date = new Date(),
): ConsentRequestStatus {
  if (request.status === 'pending' && !isConsentRequestOpen(request, now)) return 'expired';
  return request.status;
}
